<script setup lang='ts'>
import { APP_ROUTER } from '@qctsw/common'
import { carLevel, manufacturer, presetDay, useFilter } from '../hooks/filter'
import { logicReputationList } from '@/logic/reputation/page'
import type { ReputationResponse } from '@/server/types'
import { goAppPage, useGetRouteQuery } from '@/shared/function'
import { ISSUE_TYPE_CODE } from '@/shared/constants'

const router = useRouter()
const page = useGetRouteQuery('page', '1')

const {
  formRef,
  formData,
  carSelected,
  // 质量问题选项
  qualityIssues,
  getQualityIssuesHandle,

  clearFilter,
  getShowValue,
  filterParams,
  filterRouteQuery,

  // 修复时间选择器清除后数据未变动
  pickerChangeShow,
} = useFilter()

const {
  state,
  list,
  getPage,
  pagination,
} = logicReputationList()

function formatIssueStatLs(
  data: ReputationResponse['issueStatLs'],
  filter: (keyof typeof ISSUE_TYPE_CODE)[] = [4],
): {
    content: string
    tips: string
  }[] {
  if (!data)
    return []
  return data.filter(v => !filter.includes(v.issueTypeCode)).map((v) => {
    return {
      content: ISSUE_TYPE_CODE[v.issueTypeCode],
      tips: `${v.issueLabel}(${v.complainTotal})`,
    }
  })
}

function submit() {
  router.push(APP_ROUTER.reputation({ ...filterRouteQuery.value }))
}

const scrollViewRef = ref<HTMLElement>()
useActivatedWatch(() => [
  watch(filterParams, () => {
    submit()
  }, { deep: true }),
  router?.afterEach(async (to, from) => {
    // console.log(to, from)
    if (to.path === from.path) {
      let isOnlyPageQueryChange = true
      for (const key in to.query) {
        if (key !== 'page' && to.query[key] !== from.query[key]) {
          isOnlyPageQueryChange = false
          break
        }
      }

      const toPage = Number.isNaN(Number(to.query.page)) ? 1 : Number(to.query.page)
      if (!isOnlyPageQueryChange || !to.query.page) {
        pagination.initPagination()
      }
      else if (scrollViewRef.value) {
        requestAnimationFrame(() => {
          window.scrollTo({
            top: (scrollViewRef.value?.offsetTop || 0) - 100,
            behavior: 'instant',
          })
        })
      }
      getPage(toPage, filterParams.value)
    }
  }),
])

async function initHandler() {
  const promises = [
    getQualityIssuesHandle(),
    getPage(page.value, filterParams.value),
  ]
  await Promise.all(promises)
}

await initHandler()
</script>

<template>
  <div>
    <div class="p-sm border-1 border-slate-2 rounded-md relative">
      <NForm
        ref="formRef"
        :model="formData"
        :label-width="80"
        label-placement="left"
        size="medium"
      >
        <NFormItem label="时间范围" :show-feedback="false" class="mb-sm">
          <div class="flex items-center">
            <BasicRadioGroup
              v-model="formData.timeRadio"
              :options="presetDay.map((v, i) => ({ label: v.label as string, value: i }))"
              class="mr-sm"
            />
            <!-- 按钮清除后数据未变动 -->
            <NDatePicker v-if="pickerChangeShow" v-model:value="formData.timePicker" value-format="yyyy-MM-dd" type="daterange" clearable />
          </div>
        </NFormItem>
        <NFormItem label="品牌车系" :show-feedback="false" class="mb-sm">
          <BasicCarSelectFilter
            v-model:brandId="formData.carBrandId"
            v-model:seriesId="formData.carSeriesId"
            :show-footer="true"
            :select-level="2"
            @change="carSelected"
          />
        </NFormItem>
        <NFormItem label="车型属性" :show-feedback="false" class="mb-sm">
          <BasicRadioGroup v-model="formData.seriesGrade" :options="carLevel" />
        </NFormItem>
        <NFormItem label="厂商分类" :show-feedback="false" class="mb-sm">
          <BasicRadioGroup v-model="formData.productionPlace" :options="manufacturer.map((v, i) => ({ label: v, value: i }))" />
        </NFormItem>
        <NFormItem label="质量问题" :show-feedback="false" class="mb-sm">
          <BasicRadioGroup v-model="formData.issue" :options="qualityIssues" />
        </NFormItem>
        <NFormItem :show-feedback="false" label="已选条件">
          <div>
            <template v-for="value, key in formData" :key="key">
              <button
                v-if="value !== undefined && !['timeRadio', 'carBrandInfo', 'carSeriesInfo'].includes(key)"
                class="mr-sm btn-primary"
                @click="clearFilter(key)"
              >
                {{ getShowValue(key) }}
              </button>
            </template>
            <button class="btn-default" @click="clearFilter()">
              全部清除
            </button>
          </div>
        </NFormItem>
      </NForm>
      <i class="i-custom:undraw-biking?bg text-50 absolute right-2 -bottom-9 opacity-60 select-none pointer-events-none" />
    </div>
    <div ref="scrollViewRef" class="mt-6">
      <BasicTitle title="车系总排行榜" />
      <table class="mt-4 rounded-md overflow-hidden block">
        <thead class="block">
          <tr class=" grid grid-cols-36 py-sm text-white text-center bg-blue-5 font-bold">
            <td class="col-span-2">
              排名
            </td>
            <td class="col-span-4">
              品牌
            </td>
            <td class="col-span-6">
              车系
            </td>
            <td class="col-span-4">
              车型属性
            </td>
            <td class="col-span-4">
              生产方式
            </td>
            <td class="col-span-12">
              典型问题
            </td>
            <td class="col-span-4">
              投诉总量
            </td>
          </tr>
        </thead>
        <tbody class="block border-1 border-t-0 border-slate-2 divide-y-1 divide-slate-2 rounded-b-md">
          <UtilLoadState tag-name="tr" :state="state" :reload-handler="() => getPage(page, filterParams)">
            <template #loading>
              <tr v-for="index in 10" :key="index" class="h-17.5 w-full block flex items-center p-sm">
                <n-skeleton class="w-full h-full rounded-md" />
              </tr>
            </template>
            <template v-if="list.length">
              <tr
                v-for="(item, index) in list" :key="index"
                class="grid grid-cols-36 place-items-center text-center py-2.5  overflow-hidden cursor-pointer hover:bg-slate-1"
                @click="goAppPage(APP_ROUTER.reputationDetail(item.carSeriesId))"
              >
                <td class="col-span-2">
                  {{ (index + 1) + (pagination.pagination.page - 1) * pagination.pagination.pageSize }}
                </td>
                <td class="col-span-4">
                  <NEllipsis>
                    {{ item.carBrandName }}
                  </NEllipsis>
                </td>
                <td class="col-span-6">
                  <NEllipsis>
                    <NuxtLink :to="APP_ROUTER.reputationDetail(item.carSeriesId)">
                      {{ item.carSeriesName }}
                    </NuxtLink>
                  </NEllipsis>
                </td>
                <td class="col-span-4">
                  {{ item.seriesGrade }}
                </td>
                <td class="col-span-4">
                  {{ item.productionPlace }}
                </td>
                <td class="overflow-hidden col-span-12">
                  <BasicLabel :data-list="formatIssueStatLs(item.issueStatLs)" />
                </td>
                <td class="col-span-4">
                  {{ item.complainTotal }}
                </td>
              </tr>
            </template>
            <tr v-else class="h-50 w-full grid place-content-center ">
              <td>
                <BasicEmpty class="m-auto" text="暂无相关数据" />
              </td>
            </tr>
          </UtilLoadState>
          <tr v-if="pagination.pagination.pageCount" class="block">
            <BasicPagination
              tag-name="td"
              class="mb-xl mt-xl max-w-100 mx-auto"
              :page="pagination.pagination.page"
              :total="pagination.pagination.pageCount"
              :to="(page) => APP_ROUTER.reputation({ page, ...filterRouteQuery })"
            />
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style lang='scss' scoped>
:deep(*){
  .n-form-item-label--right-mark{
    --uno: text-4 font-bold;
  }
}

td {
  --uno: w-full;
}
</style>
